import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Mail, Lock, User, Phone, Eye, EyeOff, ArrowRight, 
  Loader2, CheckCircle, AlertCircle, ChevronLeft,
  Sparkles, Shield, Globe, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    login, register, registerExtended, loginWithGoogle, 
    sendMobileOTP, verifyMobileOTP, isAuthenticated, loading: authLoading 
  } = useAuth();
  
  // View state: 'options' | 'login' | 'signup' | 'mobile' | 'otp'
  const [view, setView] = useState('options');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    mobileNumber: '',
    alternateEmail: '',
    alternateMobile: ''
  });
  const [mobileForm, setMobileForm] = useState({ mobileNumber: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [demoOTP, setDemoOTP] = useState(null);
  const [showAltFields, setShowAltFields] = useState(false);
  
  // Helper function to extract error message from API response
  const getErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') {
      return detail.message || detail.error || detail.msg || JSON.stringify(detail);
    }
    return fallback;
  };
  
  // Helper to redirect after successful auth
  const redirectAfterAuth = useCallback(() => {
    const from = location.state?.from;
    const bookingData = location.state?.bookingData;
    
    // If we have booking data, redirect to complete-booking with the preserved state
    if (from === '/complete-booking' && bookingData) {
      navigate('/complete-booking', { state: bookingData, replace: true });
    } else if (from && from !== '/auth') {
      navigate(from, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [location.state, navigate]);
  
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      redirectAfterAuth();
    }
  }, [isAuthenticated, authLoading, redirectAfterAuth]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Instant redirect - no artificial delay
    loginWithGoogle();
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      redirectAfterAuth();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerExtended({
        first_name: signupForm.firstName,
        last_name: signupForm.lastName,
        email: signupForm.email,
        password: signupForm.password,
        mobile_number: signupForm.mobileNumber,
        alternate_email: signupForm.alternateEmail || null,
        alternate_mobile: signupForm.alternateMobile || null
      });
      toast.success('Account created successfully!');
      redirectAfterAuth();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!mobileForm.mobileNumber) {
      toast.error('Please enter your mobile number');
      return;
    }
    setLoading(true);
    try {
      const result = await sendMobileOTP(mobileForm.mobileNumber);
      setOtpSent(true);
      if (result.demo_mode && result.demo_otp) {
        setDemoOTP(result.demo_otp);
        toast.success(`OTP sent! Demo OTP: ${result.demo_otp}`);
      } else {
        toast.success('OTP sent to your mobile');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyMobileOTP(mobileForm.mobileNumber, mobileForm.otp);
      toast.success('Welcome to AIRA ECHO!');
      redirectAfterAuth();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid OTP'));
    } finally {
      setLoading(false);
    }
  };

  const renderOptions = () => (
    <motion.div
      key="options"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-4"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center"
        >
          <Sparkles className="w-10 h-10 text-white" />
        </motion.div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">Welcome to AIRA ECHO</h1>
        <p className="text-[var(--text-secondary)]">Your AI-powered life assistant</p>
      </div>

      {/* Google Login - PRIMARY & FASTEST OPTION */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleGoogleLogin}
        disabled={googleLoading}
        className={`w-full py-5 px-6 rounded-xl bg-white text-gray-800 font-semibold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all border-2 ${googleLoading ? 'border-[var(--color-primary)] animate-pulse' : 'border-gray-200 hover:border-[var(--color-primary)]'} disabled:cursor-wait`}
        data-testid="google-login-btn"
      >
        {googleLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin text-[var(--color-primary)]" />
            <span>Opening Google...</span>
          </>
        ) : (
          <>
            <GoogleIcon />
            <span>Continue with Google</span>
            <span className="ml-2 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Instant</span>
          </>
        )}
      </motion.button>

      {/* Divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-[var(--border-primary)]" />
        <span className="text-[var(--text-secondary)] text-sm">or</span>
        <div className="flex-1 h-px bg-[var(--border-primary)]" />
      </div>

      {/* Create Account */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setView('signup')}
        className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all"
        data-testid="create-account-btn"
      >
        <User className="w-5 h-5" />
        Create Account
      </motion.button>

      {/* Mobile Login */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setView('mobile')}
        className="w-full py-4 px-6 rounded-xl bg-[var(--bg-card)] text-[var(--text-primary)] font-semibold flex items-center justify-center gap-3 border border-[var(--border-primary)] hover:border-[var(--color-primary)] transition-all"
        data-testid="mobile-login-btn"
      >
        <Phone className="w-5 h-5" />
        Continue with Mobile Number
      </motion.button>

      {/* Existing Account Link */}
      <p className="text-center text-[var(--text-secondary)] mt-6">
        Already have an account?{' '}
        <button 
          onClick={() => setView('login')}
          className="text-[var(--color-primary)] hover:underline font-medium"
        >
          Sign in
        </button>
      </p>

      {/* Trust indicators */}
      <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-[var(--border-primary)]">
        <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs">
          <Shield className="w-4 h-4" />
          <span>Secure</span>
        </div>
        <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs">
          <Globe className="w-4 h-4" />
          <span>Global</span>
        </div>
        <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs">
          <CheckCircle className="w-4 h-4" />
          <span>Trusted</span>
        </div>
      </div>
    </motion.div>
  );

  const renderLogin = () => (
    <motion.div
      key="login"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-6"
    >
      <button 
        onClick={() => setView('options')}
        className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Welcome Back</h2>
        <p className="text-[var(--text-secondary)]">Sign in to your account</p>
      </div>

      <form onSubmit={handleEmailLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
            <input
              type="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              placeholder="you@example.com"
              required
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="login-email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              placeholder="Enter your password"
              required
              className="w-full pl-12 pr-12 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Link to="/forgot-password" className="text-sm text-[var(--color-primary)] hover:underline">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="login-submit"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              Sign In
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Google Login Shortcut */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-[var(--border-primary)]" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-[var(--bg-primary)] text-[var(--text-secondary)]">or continue with</span>
        </div>
      </div>

      <button
        onClick={handleGoogleLogin}
        className="w-full py-3 px-6 rounded-xl bg-white text-gray-800 font-medium flex items-center justify-center gap-3 border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <GoogleIcon />
        Google
      </button>
    </motion.div>
  );

  const renderSignup = () => (
    <motion.div
      key="signup"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-6"
    >
      <button 
        onClick={() => setView('options')}
        className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">Create Your Account</h2>
        <p className="text-[var(--text-secondary)]">Join the future of AI assistance</p>
      </div>

      <form onSubmit={handleSignup} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={signupForm.firstName}
              onChange={(e) => setSignupForm({ ...signupForm, firstName: e.target.value })}
              placeholder="John"
              required
              className="w-full px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="signup-firstname"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={signupForm.lastName}
              onChange={(e) => setSignupForm({ ...signupForm, lastName: e.target.value })}
              placeholder="Doe"
              required
              className="w-full px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="signup-lastname"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
            <input
              type="email"
              value={signupForm.email}
              onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
              placeholder="you@example.com"
              required
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="signup-email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Mobile Number *
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
            <input
              type="tel"
              value={signupForm.mobileNumber}
              onChange={(e) => setSignupForm({ ...signupForm, mobileNumber: e.target.value })}
              placeholder="+1 234 567 8900"
              required
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="signup-mobile"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
            Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={signupForm.password}
              onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
              placeholder="Min 8 chars, 1 uppercase, 1 number"
              required
              minLength={8}
              className="w-full pl-12 pr-12 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="signup-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Alternate Fields Toggle */}
        <button
          type="button"
          onClick={() => setShowAltFields(!showAltFields)}
          className="text-sm text-[var(--color-primary)] hover:underline flex items-center gap-1"
        >
          {showAltFields ? 'Hide' : 'Add'} backup contact details (optional)
        </button>

        <AnimatePresence>
          {showAltFields && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 overflow-hidden"
            >
              <p className="text-xs text-[var(--text-secondary)]">
                Backup contacts help recover your account if you lose access to your primary email or phone.
              </p>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                  Alternate Email
                </label>
                <input
                  type="email"
                  value={signupForm.alternateEmail}
                  onChange={(e) => setSignupForm({ ...signupForm, alternateEmail: e.target.value })}
                  placeholder="backup@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  data-testid="signup-alt-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                  Alternate Mobile
                </label>
                <input
                  type="tel"
                  value={signupForm.alternateMobile}
                  onChange={(e) => setSignupForm({ ...signupForm, alternateMobile: e.target.value })}
                  placeholder="+1 234 567 8901"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  data-testid="signup-alt-mobile"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="signup-submit"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              Create Account
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-[var(--text-secondary)]">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="text-[var(--color-primary)] hover:underline">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="text-[var(--color-primary)] hover:underline">Privacy Policy</Link>
      </p>
    </motion.div>
  );

  const renderMobileLogin = () => (
    <motion.div
      key="mobile"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="space-y-6"
    >
      <button 
        onClick={() => { setView('options'); setOtpSent(false); setDemoOTP(null); }}
        className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
          <Phone className="w-8 h-8 text-[var(--color-primary)]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          {otpSent ? 'Enter OTP' : 'Mobile Login'}
        </h2>
        <p className="text-[var(--text-secondary)]">
          {otpSent 
            ? `We sent a code to ${mobileForm.mobileNumber}` 
            : 'Quick login with your mobile number'}
        </p>
      </div>

      {!otpSent ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Mobile Number
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
              <input
                type="tel"
                value={mobileForm.mobileNumber}
                onChange={(e) => setMobileForm({ ...mobileForm, mobileNumber: e.target.value })}
                placeholder="+1 234 567 8900"
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] transition-colors text-lg"
                data-testid="mobile-input"
              />
            </div>
          </div>

          <button
            onClick={handleSendOTP}
            disabled={loading || !mobileForm.mobileNumber}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="send-otp-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Send OTP
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-xs text-[var(--text-secondary)]">
            We'll send a 6-digit verification code via SMS
          </p>
        </div>
      ) : (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          {/* Demo OTP Display */}
          {demoOTP && (
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Demo Mode</span>
              </div>
              <p className="text-amber-300 text-lg font-mono font-bold tracking-widest">
                Your OTP: {demoOTP}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Enter 6-digit OTP
            </label>
            <input
              type="text"
              value={mobileForm.otp}
              onChange={(e) => setMobileForm({ ...mobileForm, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] text-center text-2xl tracking-[1em] font-mono focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              data-testid="otp-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading || mobileForm.otp.length !== 6}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="verify-otp-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Verify & Login
                <CheckCircle className="w-5 h-5" />
              </>
            )}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => { setOtpSent(false); setDemoOTP(null); }}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Change number
            </button>
            <button
              type="button"
              onClick={handleSendOTP}
              disabled={loading}
              className="text-[var(--color-primary)] hover:underline"
            >
              Resend OTP
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] p-4">
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-[var(--color-primary)]/5 blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-[var(--color-secondary)]/5 blur-3xl" />
      </div>

      {/* Auth Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md p-8 rounded-3xl bg-[var(--bg-primary)] border border-[var(--border-primary)] shadow-2xl"
      >
        <AnimatePresence mode="wait">
          {view === 'options' && renderOptions()}
          {view === 'login' && renderLogin()}
          {view === 'signup' && renderSignup()}
          {view === 'mobile' && renderMobileLogin()}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
